import React, { useState, useEffect, useRef, useCallback } from "react";
import "./NavbarStyle.css";
import linkedin from "../../assets/linkedin.png";
import github   from "../../assets/github.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse, faLightbulb, faBriefcase,
  faCode, faFolder, faPen, faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

/* ── Sun / Moon icons ─────────────────── */
const SunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1"  x2="12" y2="3"/>  <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64"  y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1"  y1="12" x2="3"  y2="12"/> <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
    <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

/* ── Dock config ──────────────────────── */
const DOCK_ITEMS = [
  { id: null,         label: "Home",      icon: faHouse     },
  { id: "services",   label: "Expertise", icon: faLightbulb },
  { id: "experience", label: "Resume",    icon: faBriefcase },
  { id: "skill",      label: "Skills",    icon: faCode      },
  { id: "project",    label: "Projects",  icon: faFolder    },
  { id: "blog",       label: "Blog",      icon: faPen       },
  { id: "contact",    label: "Contact",   icon: faEnvelope  },
];

const MAGNIFY_RADIUS = 130; // px spread of magnification
const MAX_SCALE      = 1.8; // peak scale at cursor

export const Navbar = ({ setLoader, theme, toggleTheme }) => {
  const [scrolled,  setScrolled]  = useState(false);
  const [active,    setActive]    = useState("");
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [progress,  setProgress]  = useState(0);
  const dockRef    = useRef(null);
  const observerRef = useRef(null);

  /* scroll + progress */
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* active section tracker */
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { threshold: 0.3, rootMargin: "-10% 0px -50% 0px" }
    );
    DOCK_ITEMS.forEach(({ id }) => {
      if (id) {
        const el = document.getElementById(id);
        if (el) observerRef.current.observe(el);
      }
    });
    return () => observerRef.current?.disconnect();
  }, []);

  /* ── Magnification: track mouse over dock ── */
  const onDockMouseMove = useCallback((e) => {
    const dock = dockRef.current;
    if (!dock) return;
    const items = dock.querySelectorAll(".dock-item");
    const mouseX = e.clientX;
    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const cx   = rect.left + rect.width / 2;
      const dist = Math.abs(mouseX - cx);
      const scale = dist < MAGNIFY_RADIUS
        ? 1 + (MAX_SCALE - 1) * Math.pow(Math.cos((dist / MAGNIFY_RADIUS) * Math.PI / 2), 2)
        : 1;
      item.style.setProperty("--s", scale.toFixed(3));
      item.style.setProperty("--ty", `${-((scale - 1) * 20).toFixed(1)}px`);
    });
  }, []);

  const onDockMouseLeave = useCallback(() => {
    dockRef.current?.querySelectorAll(".dock-item").forEach(item => {
      item.style.setProperty("--s",  "1");
      item.style.setProperty("--ty", "0px");
    });
  }, []);

  /* body scroll lock when mobile menu is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  /* navigate */
  const goto = (id) => {
    setLoader(true);
    if (!id) { window.scrollTo({ top: 0, behavior: "smooth" }); }
    else { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }
    setMenuOpen(false);
  };

  return (
    <>
      {/* ══════════════════════════════════
          MINIMAL TOP BAR
      ══════════════════════════════════ */}
      <header className={`topbar ${scrolled ? "topbar--scrolled" : ""}`}>
        <div className="topbar-progress" style={{ width: `${progress}%` }} />

        <button className="topbar-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Go to top">
          <span className="logo-nf">NF</span><span className="logo-dot">.</span>
        </button>

        <div className="topbar-right">
          <a href="https://www.linkedin.com/in/sesini/" target="_blank"
             rel="noreferrer" className="topbar-icon-btn" aria-label="LinkedIn">
            <img src={linkedin} alt="LinkedIn" />
          </a>
          <a href="https://github.com/sesini1998" target="_blank"
             rel="noreferrer" className="topbar-icon-btn" aria-label="GitHub">
            <img src={github} alt="GitHub" />
          </a>
          <button className="topbar-theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === "light" ? <MoonIcon /> : <SunIcon />}
          </button>
          <button
            className={`topbar-burger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ══════════════════════════════════
          FLOATING MAGNIFYING DOCK
      ══════════════════════════════════ */}
      <div className="dock-wrapper" aria-label="Navigation dock">
        <div
          className="dock"
          ref={dockRef}
          onMouseMove={onDockMouseMove}
          onMouseLeave={onDockMouseLeave}
        >
          {DOCK_ITEMS.map(({ id, label, icon }, i) => {
            const isActive = id && active === id;
            return (
              <button
                key={label}
                className={`dock-item ${isActive ? "dock-item--active" : ""}`}
                onClick={() => goto(id)}
                aria-label={label}
                style={{ "--entry-i": i }}
              >
                {/* Icon bubble */}
                <span className="dock-bubble">
                  <FontAwesomeIcon icon={icon} className="dock-icon" />
                </span>

                {/* Text label */}
                <span className="dock-label">{label}</span>

                {/* Active dot */}
                {isActive && <span className="dock-active-dot" />}
              </button>
            );
          })}

          {/* Divider + Theme toggle */}
          <div className="dock-divider" />
          <button
            className="dock-item dock-item--theme"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            style={{ "--entry-i": DOCK_ITEMS.length + 1 }}
          >
            <span className="dock-bubble dock-bubble--theme">
              {theme === "light" ? <MoonIcon /> : <SunIcon />}
            </span>
            <span className="dock-label">{theme === "light" ? "Dark" : "Light"}</span>
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════
          MOBILE FULLSCREEN MENU
      ══════════════════════════════════ */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`}>
        <button className="mobile-menu-close" onClick={() => setMenuOpen(false)}>✕</button>
        <ul>
          {DOCK_ITEMS.map(({ id, label, icon }, i) => (
            <li key={label} style={{ "--i": i }}>
              <button onClick={() => goto(id)}>
                <FontAwesomeIcon icon={icon} />
                <span>{label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};
